import { useCallback, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Typography } from '@uniquegood/realworld-studio-design';

import { useIsAdminSelector, useModalState, useRequest, useUser } from '@/hooks';
import { isSchoolAdminRole } from '@/utils/roleAccess';

export const useChannelLimit = () => {
  const { data: { data: apps } = {} } = useRequest<CommonResponseModel<ApplicationResponseModel[]>>(
    '/apps',
    {},
    { headers: { 'x-rwd-api-version': '1.1' } }
  );
  const { data: user } = useUser();
  const { modal, openModal, closeModal } = useModalState();
  const history = useHistory();
  const { appId } = useParams<AppParam>();
  const { data: isAdmin } = useIsAdminSelector();
  const isSchoolAdmin = isSchoolAdminRole(user?.role);
  const isPrivilegedRole = Boolean(isAdmin || isSchoolAdmin);

  const isLimitExceeded = useMemo(() => {
    if (!apps || !user) {
      return false;
    }

    const myManagedApps = apps.filter((app) => {
      const member = app.members?.find((member) => member.id === user.id);
      return member?.level === 'Owner' || member?.level === 'Author';
    });

    return myManagedApps.length > 5;
  }, [apps, user]);

  const handleCloseModal = useCallback(
    (targetName: string) => {
      closeModal();
      if (targetName !== '콘텐츠') {
        history.push(`/apps/${appId}`);
      }
    },
    [closeModal]
  );

  const checkLimitAndShowModal = useCallback(
    (targetName: string) => {
      if (isLimitExceeded && user && !isPrivilegedRole) {
        openModal({
          title: '포함된 채널이 너무 많아요',
          onClose: () => handleCloseModal(targetName),
          children: (
            <>
              <Typography as="p" type="body">
                {`${user.name}`}님이 소유자 또는 편집자로 포함된 채널이 5개를 초과하여
                <br />새 콘텐츠 생성, 콘텐츠 접근, 피드 접근, 커뮤니티 접근이 불가해요!
              </Typography>
              <br />
              <Typography as="p" type="body">
                1. 편집자로 추가된 채널에서 나가시거나
                <br />
                2. 소유자 권한을 삭제하여 5개 이하로 만들어 주세요.
              </Typography>
            </>
          ),
          primaryAction: {
            content: '확인',
            onAction: () => {
              handleCloseModal(targetName);
            }
          }
        });

        return false;
      }

      return true;
    },
    [isLimitExceeded, user, isPrivilegedRole, appId, history, closeModal]
  );

  return { isLimitExceeded, checkLimitAndShowModal, modal };
};
